/**
 * ============================================================================
 * CUSTOM BUTTON COMPONENT
 * ============================================================================
 * 
 * Enhanced button component with loading states, animations, and interactions.
 * 
 * ============================================================================
 */



class CustomButton {
  constructor(element) {
    this.element = element;
    this.isLoading = false;
    this.originalText = element.textContent.trim();
    this.loadingText = data(element, 'loading-text') || 'Loading...';
    
    this.init();
  }
  
  /**
   * Initialize button
   */
  init() {
    this.bindEvents();
    console.log('🔘 Custom Button initialized');
  }
  
  /**
   * Bind events
   */
  bindEvents() {
    this.element.addEventListener('click', (e) => {
      this.handleClick(e);
    });
    
    // Handle loading state from data attribute
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-loading') {
          const isLoading = data(this.element, 'loading') === 'true';
          if (isLoading !== this.isLoading) {
            if (isLoading) {
              this.showLoading();
            } else {
              this.hideLoading();
            }
          }
        }
      });
    });
    
    observer.observe(this.element, {
      attributes: true,
      attributeFilter: ['data-loading']
    });
    
    this.observer = observer;
  }
  
  /**
   * Handle button click
   * @param {Event} e - Click event
   */
  handleClick(e) {
    // Prevent double clicks when loading
    if (this.isLoading) {
      e.preventDefault();
      return;
    }
    
    // Handle auto-loading
    if (data(this.element, 'auto-loading') === 'true') {
      this.showLoading();
      
      // Auto-hide after timeout
      const timeout = parseInt(data(this.element, 'loading-timeout')) || 3000;
      setTimeout(() => {
        this.hideLoading();
      }, timeout);
    }
    
    // Emit custom event
    this.element.dispatchEvent(new CustomEvent('button:click', {
      detail: { button: this.element }
    }));
  }
  
  /**
   * Show loading state
   */
  showLoading() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    
    // Add loading class
    addClass(this.element, 'btn-loading');
    
    // Disable button
    this.element.disabled = true;
    attr(this.element, 'aria-busy', 'true');
    
    // Update text
    if (this.loadingText) {
      this.element.textContent = this.loadingText;
    }
    
    // Emit event
    this.element.dispatchEvent(new CustomEvent('button:loading:start', {
      detail: { button: this.element }
    }));
  }
  
  /**
   * Hide loading state
   */
  hideLoading() {
    if (!this.isLoading) return;
    
    this.isLoading = false;
    
    // Remove loading class
    removeClass(this.element, 'btn-loading');
    
    // Enable button
    this.element.disabled = false;
    attr(this.element, 'aria-busy', 'false');
    
    // Restore text
    this.element.textContent = this.originalText;
    
    // Emit event
    this.element.dispatchEvent(new CustomEvent('button:loading:end', {
      detail: { button: this.element }
    }));
  }
  
  /**
   * Set loading state
   * @param {boolean} loading - Loading state
   */
  setLoading(loading) {
    if (loading) {
      this.showLoading();
    } else {
      this.hideLoading();
    }
  }
  
  /**
   * Update button text
   * @param {string} text - New text
   */
  setText(text) {
    this.originalText = text;
    if (!this.isLoading) {
      this.element.textContent = text;
    }
  }
  
  /**
   * Update loading text
   * @param {string} text - New loading text
   */
  setLoadingText(text) {
    this.loadingText = text;
    if (this.isLoading) {
      this.element.textContent = text;
    }
  }
  
  /**
   * Enable button
   */
  enable() {
    this.element.disabled = false;
    removeClass(this.element, 'disabled');
  }
  
  /**
   * Disable button
   */
  disable() {
    this.element.disabled = true;
    addClass(this.element, 'disabled');
  }
  
  /**
   * Check if button is loading
   * @returns {boolean} True if loading
   */
  isLoadingState() {
    return this.isLoading;
  }
  
  /**
   * Destroy button component
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Remove event listeners
    this.element.removeEventListener('click', this.handleClick);
    
    // Reset state
    this.hideLoading();
    this.enable();
  }
}
